/**
 * BusinessMagicLinkInvite — small panel embeddable inside the
 * business edit page. Business owner types a staff email + role,
 * and we send them a magic-link sign-in.
 *
 * Uses the shared `http` axios instance so the JWT is picked up
 * automatically from the AuthContext bootstrap — no need to read
 * the raw token from the hook.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, Trash2, Mail, Clock, CheckCircle2, XCircle } from "lucide-react";
import { http } from "@/lib/api";

export default function BusinessMagicLinkInvite({ businessId, businessName }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("Staff");
  const [sending, setSending] = useState(false);
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    if (!businessId) return;
    try {
      const r = await http.get(`/businesses/${businessId}/staff/magic-links`);
      setItems(r.data.items || []);
    } catch { /* silent — panel is optional */ }
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    const clean = email.trim();
    if (!clean) return;
    setSending(true);
    try {
      await http.post(`/businesses/${businessId}/staff/magic-links`, {
        email: clean,
        role_label: role.trim() || "Staff",
      });
      toast.success(`Invitation sent to ${clean}`);
      setEmail("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const revoke = async (staffEmail) => {
    try {
      await http.post(`/businesses/${businessId}/staff/magic-links/revoke`, { email: staffEmail });
      toast.success("Revoked");
      load();
    } catch {
      toast.error("Failed to revoke");
    }
  };

  const statusIcon = (s) =>
    s === "consumed" ? <CheckCircle2 className="w-3 h-3 text-green-600" />
    : s === "revoked" ? <XCircle className="w-3 h-3 text-red-600" />
    : s === "pending" ? <Clock className="w-3 h-3 text-amber-600" />
    : null;

  return (
    <section className="wofy-card !p-5" data-testid="magic-link-invite-panel">
      <h2 className="font-display text-xl mb-1 flex items-center gap-2">
        <Mail className="w-5 h-5" />Invite staff (magic link)
      </h2>
      <p className="text-sm opacity-70 mb-4">
        Passwordless sign-in for your team. They get an email with a one-time link (30 min expiry)
        and land directly on the {businessName || "business"} dashboard.
      </p>

      <form onSubmit={invite} className="grid gap-2 sm:grid-cols-[1fr_auto_auto] mb-4">
        <input
          required type="email" value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="staff@example.com"
          className="px-3 py-2 rounded-lg border"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="staff-email-input"
        />
        <input
          value={role} onChange={(e) => setRole(e.target.value)} maxLength={60}
          placeholder="Role label (e.g. Manager)"
          className="px-3 py-2 rounded-lg border w-full sm:w-52"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="staff-role-input"
        />
        <button
          type="submit" disabled={sending || !email.trim()}
          className="wofy-btn-primary inline-flex items-center gap-2 disabled:opacity-40"
          data-testid="staff-invite-btn"
        >
          {sending
            ? <><Loader2 className="w-4 h-4 animate-spin" />Sending…</>
            : <><Send className="w-4 h-4" />Send link</>}
        </button>
      </form>

      {items.length > 0 && (
        <div>
          <div className="overline text-xs opacity-70 mb-2">Recent invitations</div>
          <ul className="text-sm space-y-1">
            {items.map((it, i) => (
              <li key={i} className="p-2 rounded flex items-center gap-2 flex-wrap"
                  style={{ background: "var(--wofy-surface)" }}>
                <span className="font-mono text-xs">{it.staff_email}</span>
                <span className="text-xs opacity-70">· {it.role_label || "Staff"}</span>
                <span className="text-xs opacity-70 inline-flex items-center gap-1">
                  {statusIcon(it.status)}{it.status}
                </span>
                <span className="ml-auto text-[10px] opacity-60">
                  {new Date(it.created_at).toLocaleString()}
                </span>
                {it.status === "pending" && (
                  <button onClick={() => revoke(it.staff_email)}
                    className="text-xs text-red-600 inline-flex items-center gap-0.5 hover:underline"
                    data-testid={`revoke-${it.staff_email}`}>
                    <Trash2 className="w-3 h-3" />Revoke
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
